import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Clipboard, Edit3, Landmark, LockKeyhole, Save } from "lucide-react";
import { toast } from "sonner";
import { PixQrUploader } from "@/components/PixQrUploader";
import { FormAlert, inputCls } from "@/components/FormBits";
import { TopBar } from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";

const emptyConfig = {
  account_name: "",
  pix_copy_paste: "",
  has_qr_code: false,
  qr_filename: null,
};

export default function Pix() {
  const [config, setConfig] = useState(null);
  const [accountName, setAccountName] = useState("");
  const [pixCopyPaste, setPixCopyPaste] = useState("");
  const [editing, setEditing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const previewRef = useRef("");

  const clearPreview = useCallback(() => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = "";
    setPreviewUrl("");
  }, []);

  const loadPreview = useCallback(async () => {
    clearPreview();
    setLoadingPreview(true);
    try {
      const response = await api.get("/pix/qr-code", { responseType: "blob" });
      const nextUrl = URL.createObjectURL(response.data);
      previewRef.current = nextUrl;
      setPreviewUrl(nextUrl);
    } catch (requestError) {
      const errorBlob = requestError?.response?.data;
      if (errorBlob instanceof Blob) {
        try {
          const errorBody = JSON.parse(await errorBlob.text());
          setError(errorBody.detail || "Não foi possível carregar a imagem.");
        } catch (parseError) {
          setError("Não foi possível carregar a imagem.");
        }
      } else {
        setError(formatApiError(requestError));
      }
    } finally {
      setLoadingPreview(false);
    }
  }, [clearPreview]);

  const applyConfig = useCallback(async (nextConfig, refreshPreview = false) => {
    setConfig(nextConfig);
    setAccountName(nextConfig.account_name || "");
    setPixCopyPaste(nextConfig.pix_copy_paste || "");
    setEditing(!nextConfig.pix_copy_paste);
    if (refreshPreview && nextConfig.has_qr_code) await loadPreview();
    if (!nextConfig.has_qr_code) clearPreview();
  }, [clearPreview, loadPreview]);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/pix");
      await applyConfig(data, data.has_qr_code);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setConfig(emptyConfig);
    }
  }, [applyConfig]);

  useEffect(() => {
    load();
    return clearPreview;
  }, [clearPreview, load]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/pix", {
        account_name: accountName,
        pix_copy_paste: pixCopyPaste,
      });
      await applyConfig(data);
      setEditing(false);
      toast.success("Informações Pix salvas.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const updateQrConfig = async (nextConfig) => {
    setConfig((current) => ({ ...current, ...nextConfig }));
    if (nextConfig.has_qr_code) {
      await loadPreview();
    } else {
      clearPreview();
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pixCopyPaste);
      toast.success("Pix Copia e Cola copiado.");
    } catch (copyError) {
      toast.error("Não foi possível copiar. Selecione o texto para copiar.");
    }
  };

  if (!config) {
    return <div data-testid="pix-loading" className="grid min-h-screen place-items-center cofre-bg text-xl font-bold text-emerald-900">Carregando Meu Pix...</div>;
  }

  return (
    <div data-testid="pix-page" className="min-h-screen cofre-bg">
      <TopBar />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
        <section className="fade-up">
          <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Sua conta</p>
          <h1 className="mt-2 font-heading text-4xl font-extrabold text-emerald-950 sm:text-5xl">Meu Pix</h1>
          <p className="mt-3 max-w-2xl text-lg text-slate-700">Cadastre seus dados manualmente. Eles ficam visíveis somente para você.</p>
        </section>
        <div data-testid="pix-manual-status" className="mt-8 flex gap-3 rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 text-amber-950">
          <LockKeyhole className="mt-0.5 h-6 w-6 shrink-0 text-amber-800" />
          <div><p className="font-bold">Modo manual ativo</p><p className="mt-1 text-base">Nenhuma conta bancária está conectada. A integração automática pode ser adicionada no futuro.</p></div>
        </div>
        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <PixQrUploader config={config} previewUrl={previewUrl} loadingPreview={loadingPreview} onUpdated={updateQrConfig} />
          <section data-testid="pix-copy-section" className="rounded-3xl border-2 border-emerald-200 bg-white p-6 shadow-sm">
            <p className="text-sm font-bold uppercase tracking-wide text-emerald-800">Dados da conta</p>
            <h2 className="mt-1 font-heading text-2xl font-extrabold text-emerald-950">Pix Copia e Cola</h2>
            <label htmlFor="pix-account-name" className="mt-6 block text-lg font-bold text-slate-900">Nome da conta <span className="font-normal text-slate-500">(opcional)</span></label>
            <div className="relative mt-2"><Landmark className="absolute left-4 top-4 h-6 w-6 text-emerald-700" /><input id="pix-account-name" data-testid="pix-account-name-input" disabled={!editing} value={accountName} onChange={(event) => setAccountName(event.target.value)} placeholder="Ex.: Minha conta Nubank" className={`${inputCls} pl-12 disabled:bg-slate-100 disabled:text-slate-600`} /></div>
            <label htmlFor="pix-copy-paste" className="mt-6 block text-lg font-bold text-slate-900">Código Pix Copia e Cola</label>
            <textarea id="pix-copy-paste" data-testid="pix-copy-paste-input" disabled={!editing} value={pixCopyPaste} onChange={(event) => setPixCopyPaste(event.target.value)} maxLength={4000} placeholder="Cole aqui o código Pix da sua conta" className="mt-2 min-h-[190px] w-full resize-y rounded-xl border-2 border-slate-300 bg-white p-4 font-mono text-base text-slate-900 placeholder:font-sans placeholder:text-slate-400 focus:border-emerald-600 focus:outline-none focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-600" />
            <FormAlert testId="pix-form-error">{error}</FormAlert>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {editing ? <button type="button" onClick={save} disabled={saving} data-testid="pix-save-button" className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl bg-emerald-700 px-5 text-lg font-bold text-white shadow-[0_5px_0_#064e3b] hover:bg-emerald-800 disabled:opacity-60"><Save className="h-5 w-5" />{saving ? "Salvando..." : "Salvar"}</button> : <button type="button" onClick={() => setEditing(true)} data-testid="pix-edit-button" className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl bg-emerald-700 px-5 text-lg font-bold text-white shadow-[0_5px_0_#064e3b] hover:bg-emerald-800"><Edit3 className="h-5 w-5" />Editar</button>}
              <button type="button" onClick={copy} disabled={!pixCopyPaste} data-testid="pix-copy-button" className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl border-2 border-emerald-200 bg-white px-5 text-lg font-bold text-emerald-900 hover:bg-emerald-50 disabled:opacity-50"><Clipboard className="h-5 w-5" />Copiar código</button>
            </div>
            {!editing && <p data-testid="pix-saved-indicator" className="mt-4 flex items-center gap-2 text-base font-bold text-emerald-800"><Check className="h-5 w-5" />Informações salvas</p>}
          </section>
        </div>
      </main>
    </div>
  );
}